import { legalConfig as cfg } from '../legalConfig';
import { LegalDoc } from '../types';

/**
 * PAIA Manual (Promotion of Access to Information Act 2 of 2000, section 51),
 * with the POPIA processing description folded in. This is a founder draft
 * modelled on the standard section-51 structure — reconcile it against the
 * Information Regulator's official template and have it reviewed before relying
 * on it. Contact/fee details are [PLACEHOLDER]s in legalConfig.ts.
 */
export const paiaDoc: LegalDoc = {
  slug: 'paia',
  title: 'PAIA Manual',
  effectiveDate: cfg.effectiveDate,
  lastUpdated: cfg.lastUpdated,
  intro: [
    `This manual is published in terms of section 51 of the Promotion of Access to Information Act 2 of 2000 (PAIA). It explains what records ${cfg.entityName} (trading as ${cfg.brandName}) holds and how you may request access to them. It also describes how we process personal information under the Protection of Personal Information Act 4 of 2013 (POPIA).`,
    'This is a founder draft pending legal review and reconciliation with the Information Regulator’s official template.',
  ],
  sections: [
    {
      heading: '1. The business and Information Officer',
      blocks: [
        {
          type: 'ul',
          items: [
            `Business: ${cfg.entityName} (${cfg.entityForm}), trading as ${cfg.brandName}`,
            `Registration number: ${cfg.registrationNumber}`,
            `Registered / principal address: ${cfg.physicalAddress}`,
            `Telephone: ${cfg.telephone}`,
            `Website: ${cfg.websiteUrl}`,
            `Information Officer: ${cfg.informationOfficer}`,
            `Information Officer email: ${cfg.informationOfficerEmail}`,
          ],
        },
        {
          type: 'p',
          text: 'The Information Officer is registered with the Information Regulator and is responsible for PAIA requests and POPIA compliance.',
        },
      ],
    },
    {
      heading: '2. The Information Regulator and section 10 Guide',
      blocks: [
        {
          type: 'p',
          text: `The Information Regulator has compiled a guide, in terms of section 10 of PAIA, on how to use the Act. It is available from the Information Regulator at ${cfg.infoRegulatorUrl}.`,
        },
        {
          type: 'ul',
          items: [
            'The Information Regulator (South Africa)',
            'Website: ' + cfg.infoRegulatorUrl,
            'Email: inforeg@inforegulator.org.za (PAIA) / POPIAComplaints@inforegulator.org.za (POPIA)',
          ],
        },
      ],
    },
    {
      heading: '3. Categories of records held',
      blocks: [
        {
          type: 'p',
          text: `${cfg.brandName} holds the following broad categories of records. Some are automatically available and some are available on request, subject to PAIA and POPIA.`,
        },
        {
          type: 'ul',
          items: [
            'User (roommate seeker) records: account details, profile information, preferences, matches, likes/passes, and compatibility data.',
            'Landlord / property records: landlord or representative details, property listings, and evidence of authority to let.',
            'Communications: match chats, property inquiry conversations (landlord + matched pair), attachments, and support requests.',
            'Trust & safety records: reports, blocks, moderation decisions, and evidence gathered during investigations.',
            'Technical records: device and usage information, security logs, and cookie/analytics data (analytics only with consent).',
            'Business records: company, financial, tax, and contractual records; and employee/contractor records if applicable.',
            'Payment records: only if paid services are later introduced.',
          ],
        },
      ],
    },
    {
      heading: '4. Records available in terms of other legislation',
      blocks: [
        {
          type: 'p',
          text: 'Certain records are kept or made available under other laws, including (as applicable) the Companies Act, the Tax Administration Act, the Basic Conditions of Employment Act and Labour Relations Act (for employees), the Consumer Protection Act, and the Electronic Communications and Transactions Act.',
        },
      ],
    },
    {
      heading: '5. How to request access to a record',
      blocks: [
        {
          type: 'ul',
          items: [
            `Submit a request to the Information Officer at ${cfg.informationOfficerEmail} using the prescribed PAIA request form (Form 2 of the PAIA Regulations).`,
            'Provide enough detail to identify the record and the right you seek to exercise or protect, and how you would like to receive it.',
            `Pay the prescribed request and access fees where applicable (${cfg.paiaRequestFee}). We will tell you the access fee before processing.`,
            'We will respond within the timeframes set by PAIA (generally 30 days, extendable in the circumstances the Act allows).',
          ],
        },
        {
          type: 'p',
          text: 'If the request is for your own personal information, we may require verification of your identity before releasing records.',
        },
      ],
    },
    {
      heading: '6. Grounds for refusing access',
      blocks: [
        {
          type: 'p',
          text: 'Access may be refused on the grounds set out in PAIA, including the mandatory protection of another person’s privacy, third-party commercial information, confidential information, safety of individuals, and legally privileged records. Where only part of a record may be refused, we will give access to the remainder where reasonably possible.',
        },
      ],
    },
    {
      heading: '7. Remedies',
      blocks: [
        {
          type: 'p',
          text: `If a request is refused or you are unhappy with how it was handled, you may lodge a complaint with the Information Regulator, or apply to a court, as provided in PAIA. The Regulator’s contact details are above and at ${cfg.infoRegulatorUrl}.`,
        },
      ],
    },
    {
      heading: '8. Processing of personal information (POPIA)',
      blocks: [
        {
          type: 'p',
          text: `${cfg.entityName} is the responsible party for personal information processed through ${cfg.brandName}. Full detail is in our Privacy Policy; in summary:`,
        },
        {
          type: 'ul',
          items: [
            'Purpose: to provide roommate discovery, matching, chat, landlord inquiry conversations, safety, support, and to comply with law.',
            'Categories of data subjects: roommate seekers, landlords/property representatives, and website/app visitors.',
            'Categories of personal information: identity and contact details, profile and lifestyle preferences, location and budget, property/listing details, communications, and technical/usage data.',
            'Recipients: service providers (hosting, authentication, messaging, analytics, support, security), and legal/safety recipients where lawful.',
            `Cross-border flows: information is processed in ${cfg.processingCountries}, subject to a lawful transfer basis under POPIA.`,
            'Security: appropriate, reasonable technical and organisational measures, with breach notification where required.',
            'Retention: as set out in the Privacy Policy retention schedule.',
          ],
        },
      ],
    },
    {
      heading: '9. Availability and updates of this manual',
      blocks: [
        {
          type: 'p',
          text: `This manual is available on the ${cfg.brandName} website, at our registered address on request, and is provided to the Information Regulator as required. We will update it when our records, processing, or contact details change.`,
        },
      ],
    },
  ],
};
